/**
 * Shared field and column builders.
 *
 * Twenty-odd document types repeat the same handful of shapes — a document
 * date, a project lookup, a quantity-times-rate line grid. Naming them once
 * keeps each definition to what is actually distinctive about that document.
 */
import type { ColumnDef, FieldDef, FilterDef, LineColumnDef, LineGridDef, LookupScopeSource } from './types.js'

type W = 'full' | 'half'

export const f = {
  date: (key: string, label: string, required = false, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'date', required, width }),
  text: (key: string, label: string, width: W = 'half', required = false): FieldDef =>
    ({ key, label, control: 'text', required, width }),
  notes: (key = 'remarks', label = 'Remarks'): FieldDef =>
    ({ key, label, control: 'textarea', width: 'full' }),
  look: (key: string, label: string, entity: string, required = false, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'lookup', required, width, lookup: { entity } }),
  choice: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'select', required, width, options: options.map(([value, l]) => ({ value, label: l })) }),
  radio: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true): FieldDef =>
    ({ key, label, control: 'radio', required, width: 'full', options: options.map(([value, l]) => ({ value, label: l })) }),
  yesNo: (key: string, label: string, hint?: string): FieldDef =>
    ({ key, label, control: 'checkbox', width: 'half', ...(hint ? { hint } : {}) }),
  qty: (key: string, label: string, required = false): FieldDef =>
    ({ key, label, control: 'qty', required, width: 'half' }),
  money: (key: string, label: string, required = false): FieldDef =>
    ({ key, label, control: 'money', required, width: 'half' }),
}

/**
 * Narrows an address or contact lookup to one owner's records (BILL-008).
 *
 * `scoped(f.look('supplierAddressId', …, 'address'), ofParty('supplierId'))`
 * lists only the chosen supplier's addresses, says "No entries" until one is
 * chosen, and — through the create-defaults hook — starts on that supplier's
 * default. `OURS` lists the company's own offices, stores and sites, and
 * starts on the registered office.
 */
export const OURS: readonly LookupScopeSource[] = [{ owner: 'OURS' }]
export const ofParty = (field: string): readonly LookupScopeSource[] => [{ owner: 'PARTY', field }]
/** A party's records first, then our own — a ship-to that may be the customer's or our site. */
export const ofPartyOrOurs = (field: string): readonly LookupScopeSource[] => [{ owner: 'PARTY', field }, { owner: 'OURS' }]
export const scoped = (
  field: FieldDef, scope: readonly LookupScopeSource[], prefill?: FieldDef['prefill'],
): FieldDef => ({
  ...field,
  lookup: { ...(field.lookup ?? { entity: 'address' }), scope },
  ...(prefill ? { prefill } : {}),
})

/** The fields nearly every transactional document carries. */
export const taxUnit = (required = true): FieldDef => f.look('taxUnitId', 'Tax unit', 'tax_unit', required)
/**
 * The tax unit as the header chip beside General Info, not a question in the
 * form (PROJ1-004, INV-005, STK-002): the create wizard asks it, or the server
 * defaults it from the company's default or the project, and Action › Change
 * the tax unit changes it. Not required here — the form never draws it, so it
 * could never be answered here either.
 */
export const taxUnitChip = (): FieldDef => ({ ...taxUnit(false), chip: true })
export const jobType = (): FieldDef => f.look('jobTypeId', 'Job type', 'job_type')
export const interstate = (): FieldDef =>
  f.yesNo('interstate', 'Interstate (IGST)?', 'Set from the supplier and tax unit states. Change it only if you are sure.')

/**
 * The currency a document is written in, and what it is worth (docs/20 D8).
 *
 * Put on the documents a foreign supplier or customer is a party to. The rate
 * is asked for only when the currency is not the books' own — which is the
 * whole of the multi-currency UI, because everything downstream reads the base
 * figures the server derives from these two answers.
 */
export const currencySection = (n = '95'): import('./types.js').SectionDef => ({
  key: 'currency', n, title: 'Currency',
  fields: [
    { key: 'currencyId', label: 'Currency', control: 'lookup', width: 'half',
      lookup: { entity: 'currency' },
      hint: 'Leave it empty for the currency this company keeps its books in.' },
    { key: 'forexRate', label: 'Rate against the base currency', control: 'rate', width: 'half',
      visibleWhen: { field: 'currencyId', isSet: true },
      hint: 'What one unit of that currency is worth. Needed only for a foreign currency.' },
  ],
})

/** A terms-and-conditions card: pick a template, then edit what it put there. */
export const termsSection = (n = '90'): import('./types.js').SectionDef => ({
  key: 'terms', n, title: 'Terms and conditions',
  fields: [
    f.look('termsTemplateId', 'Terms template', 'terms_template', false, 'half'),
    // Formatted, as the reference's editor is: bold, italic, underline, lists
    // and links, kept as sanitised HTML and printed as such (BILL-015).
    { key: 'termsContent', label: 'Terms', control: 'richtext', width: 'full',
      hint: 'Picking a template fills this in. Edit it here and only this document changes.' },
  ],
})

export const col = {
  seq: (): LineColumnDef => ({ key: 'seq', label: '#', width: 46 }),
  item: (label = 'Material', editable = true): LineColumnDef =>
    ({ key: 'itemId', label, type: 'text', editable }),
  desc: (): LineColumnDef => ({ key: 'description', label: 'Description', type: 'text', secondary: true, editable: true }),
  brand: (label = 'Brand'): LineColumnDef => ({ key: 'brand', label, type: 'text', width: 116, editable: true }),
  qty: (label = 'Qty', editable = true): LineColumnDef =>
    ({ key: 'quantity', label, type: 'qty', width: 104, align: 'right', editable }),
  /**
   * Typed, or taken from the material when one is picked. It was read-only, so
   * a line added by hand could never carry a unit at all.
   */
  unit: (editable = true): LineColumnDef => ({ key: 'unit', label: 'Unit', type: 'text', width: 86, editable }),
  rate: (label = 'Rate', editable = true): LineColumnDef =>
    ({ key: 'rate', label, type: 'rate', width: 112, align: 'right', editable }),
  // "Discount" and "HSN Code", as the reference heads them on every priced grid (PUR-030).
  discount: (): LineColumnDef => ({ key: 'discountPct', label: 'Discount', type: 'rate', width: 100, align: 'right', editable: true }),
  amount: (label = 'Amount'): LineColumnDef => ({ key: 'amount', label, type: 'money', width: 136, align: 'right' }),
  ordered: (): LineColumnDef => ({ key: 'orderedQty', label: 'Ordered', type: 'qty', width: 104, align: 'right' }),
  open: (): LineColumnDef => ({ key: 'openQty', label: 'Still open', type: 'qty', width: 108, align: 'right' }),
  boqRef: (): LineColumnDef => ({ key: 'boqRef', label: 'BOQ ref', type: 'text', width: 120 }),
  hsn: (): LineColumnDef => ({ key: 'hsnSacCode', label: 'HSN Code', type: 'text', width: 118, editable: true }),
  /**
   * "Tax", read as the GST slab — "GST @ 18%" — and picked from the company's
   * slabs (INV-001, PROJ1-039, PUR-030). Still `taxPct` underneath, which is
   * what every total, split, posting and print reads; see `taxSlab`.
   */
  tax: (): LineColumnDef => ({ key: 'taxPct', label: 'Tax', type: 'rate', width: 140, align: 'left', editable: true, taxSlab: true }),
  /** docs/24 §3.8 — which rack or bin a lot went into. */
  bin: (): LineColumnDef => ({
    key: 'binId', label: 'Bin', type: 'text', width: 150, editable: true, lookup: { entity: 'storage_bin' },
    hint: 'Where in the store it was put. The lot picker and the stock count read it.',
  }),
  /**
   * Whether the customer is charged for this line.
   *
   * It is set once on the estimate and then travels — request, order, receipt,
   * invoice, challan — because the people keying those documents cannot
   * classify five hundred bolts and washers from memory, and a challan that
   * mixes the two cannot be given to a customer to sign
   * (docs/video-notes/06 §2).
   */
  billable: (label = 'Type'): LineColumnDef => ({
    key: 'isBillable', label, type: 'text', width: 124, editable: true,
    options: [{ value: 'YES', label: 'Billable' }, { value: 'NO', label: 'Non billable' }],
  }),
}

/**
 * Additional charges, and the deductions and additions applied after them.
 *
 * Every priced document in the observed system carries both, and without them
 * freight, retention and an advance recovery have nowhere to go.
 */
export const chargesGrid = (n = '80', sources: LineGridDef['sources'] = []): LineGridDef => ({
  section: 'CHARGE', title: 'Additional charges', n,
  addLabel: 'Add a charge',
  columns: [
    col.seq(),
    // Travelling, handling, transport… — items of type Charge, not materials.
    { key: 'itemId', label: 'Charge', type: 'text', editable: true, lookup: { entity: 'charge' } },
    col.desc(), col.qty(), col.rate(), col.hsn(), col.tax(), col.amount(),
  ],
  sources,
})

/** Which way an adjustment moves the total. Anything else reads as a deduction. */
export const ADJUSTMENT_DIRECTIONS = [
  { value: 'DEDUCTION', label: 'Deduction' },
  { value: 'ADDITION', label: 'Addition' },
] as const

/**
 * Retention, advance recovery, TDS, penalties — and the occasional addition.
 *
 * The amount is typed: an adjustment is a sum, not a quantity at a rate. It
 * used to be a quantity-times-rate preview on a grid with neither column, so
 * every adjustment was ₹0, and its direction was free text nothing read.
 */
export const adjustmentsGrid = (n = '81'): LineGridDef => ({
  section: 'ADJUSTMENT', title: 'Deductions and additions', n,
  addLabel: 'Add an adjustment',
  totalsAs: 'SUBTRACT',
  columns: [
    col.seq(),
    { key: 'adjustmentId', label: 'Adjustment', type: 'text', editable: true,
      lookup: { entity: 'invoice_adjustment', fills: { direction: 'direction', defaultPct: 'percentage' } } },
    col.desc(),
    // TDS-style adjustments are a percentage of the subtotal (PUR-003): given
    // one, the amount is worked out on save; left empty, the amount is typed.
    { key: 'percentage', label: 'Percentage', type: 'rate', width: 120, align: 'right', editable: true,
      hint: 'Of the items’ subtotal. The amount is worked out from it on save.' },
    { key: 'direction', label: 'Deduction or addition', type: 'text', width: 170, editable: true,
      options: ADJUSTMENT_DIRECTIONS },
    { key: 'rate', label: 'Amount', type: 'rate', width: 150, align: 'right', editable: true },
  ],
  sources: [],
})

/**
 * A priced line grid: quantity times rate, less a discount. The reference's
 * order — Item, Order Brand, Quantity, Rate, Discount, Amount, HSN Code, Tax
 * (PUR-030): the amount reads straight after the figures it comes from.
 */
export const pricedLines = (title: string, n: string, itemLabel = 'Material'): LineGridDef => ({
  title, n,
  columns: [col.seq(), col.item(itemLabel), col.desc(), col.brand('Order brand'), col.qty(), col.unit(), col.rate(), col.discount(), col.amount(), col.billable(), col.hsn(), col.tax()],
  sources: [
    { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
    { key: 'excel', label: 'Import from Excel', source: 'excel' },
  ],
})

/**
 * Ordered in one unit, billed in another (PUR-048): steel ordered in pieces
 * and billed by the kilogram. With a billing unit, quantity and rate, the
 * amount is the billing quantity at the billing rate and the line's own rate
 * becomes what one order unit comes to. An RFQ asks only for the unit the
 * rate is to be quoted in.
 */
export const billingColumns = (unitOnly = false): LineColumnDef[] => [
  { key: 'billingUnit', label: unitOnly ? 'Rate in' : 'Billing unit', type: 'text', width: 104, editable: true,
    hint: unitOnly
      ? 'The unit the supplier is to quote the rate in, when it is not the order unit.'
      : 'Billed in a different unit? Name it, with the quantity and rate in it. Leave empty to bill in the order unit.' },
  ...(unitOnly ? [] : [
    { key: 'billingQty', label: 'Billing qty', type: 'qty' as const, width: 110, align: 'right' as const, editable: true },
    { key: 'billingRate', label: 'Billing rate', type: 'rate' as const, width: 120, align: 'right' as const, editable: true },
  ]),
]

/** The same grid with the billing-unit columns after its rate (PUR-048). */
export const withBillingUnit = (grid: LineGridDef, unitOnly = false): LineGridDef => {
  const at = grid.columns.findIndex((c) => c.key === 'rate')
  const columns = [...grid.columns]
  columns.splice(at < 0 ? columns.length : at + 1, 0, ...billingColumns(unitOnly))
  return { ...grid, columns }
}

/**
 * A purchase invoice line's "Item Info" (PUR-005): the brands the estimate
 * allows, the order it bills, the job it is for, the BOQ line it traces to
 * ("2 | 1 | T | Supply, installation…") and whether it was estimated at all —
 * the last three worked out on read from the line's source chain — and the
 * billing unit it may be billed in.
 *
 * The grid shows what the reference's Item Info grid does — Item, Quantity,
 * Rate, Discount, Amount, HSN Code, Tax — and the rest is asked in the row
 * dialog, in the reference's order (PROJ1-041, PROJ2-029): "Billed in
 * Different Unit?" opens the billing unit, quantity and rate, and "For BOQ /
 * Project?" says whether the line traces to a BOQ line or only to the job.
 */
export const withInvoiceLineInfo = (grid: LineGridDef): LineGridDef => {
  const of = (key: string): LineColumnDef | undefined => grid.columns.find((c) => c.key === key)
  const billedOther = { column: 'billedInOtherUnit', equals: ['YES'] } as const
  const picked = (key: string, extra: Partial<LineColumnDef> = {}): LineColumnDef[] => {
    const c = of(key)
    return c ? [{ ...c, ...extra }] : []
  }
  const columns: LineColumnDef[] = [
    ...picked('seq'), ...picked('itemId'),
    { key: 'allowedBrands', label: 'Allowed brands', type: 'text', width: 140, dialogOnly: true },
    ...picked('brand', { dialogOnly: true }),
    ...picked('description'),
    ...picked('unit'),
    ...picked('quantity', { label: 'Quantity' }),
    // Derived on read from the billing unit (invoice-line-info.ts); switching
    // it off clears the billing figures, so the line bills in its own unit.
    { key: 'billedInOtherUnit', label: 'Billed in different unit?', type: 'text', width: 120, editable: true,
      dialogOnly: true, control: 'radio',
      options: [{ value: 'NO', label: 'No' }, { value: 'YES', label: 'Yes' }],
      sets: { NO: { billingUnit: '', billingQty: '', billingRate: '' } } },
    ...billingColumns().map((c) => ({ ...c, dialogOnly: true, visibleWhen: billedOther })),
    ...picked('rate'), ...picked('discountPct'), ...picked('amount'), ...picked('hsnSacCode'), ...picked('taxPct'),
    { key: 'sourceOrderNo', label: 'Purchase order', type: 'text', width: 160, dialogOnly: true },
    { key: 'forProjectId', label: 'Project', type: 'text', width: 180, editable: true, lookup: { entity: 'project' },
      dialogOnly: true, hint: 'The job this line is billed to, when it is not the invoice’s own.' },
    // Worked out on read: a line that traces to a BOQ line is for that line.
    { key: 'forScope', label: 'For BOQ / Project?', type: 'text', width: 140, dialogOnly: true, control: 'radio',
      options: [{ value: 'BOQ', label: 'BOQ line' }, { value: 'PROJECT', label: 'Project' }] },
    ...picked('isBillable', { dialogOnly: true, control: 'radio' }),
    { key: 'boqLineLabel', label: 'BOQ line', type: 'text', width: 260, dialogOnly: true,
      visibleWhen: { column: 'forScope', equals: ['BOQ'] } },
    { key: 'estimationState', label: 'Estimation', type: 'text', width: 130, dialogOnly: true,
      badges: { 'Not estimated': 'bad' } },
  ]
  // Anything the base grid carries that is not placed above keeps its place at the end.
  const placed = new Set(columns.map((c) => c.key))
  return { ...grid, columns: [...columns, ...grid.columns.filter((c) => !placed.has(c.key))] }
}

/** A movement grid: quantities only, no money. Goods receipts, transfers. */
export const quantityLines = (title: string, n: string, qtyLabel = 'Quantity'): LineGridDef => ({
  title, n,
  columns: [
    col.seq(), col.item(), col.desc(), col.brand(), col.qty(qtyLabel), col.unit(),
    // A stock transfer to a customer site is a taxable event, so it is priced.
    col.rate(), col.billable(), col.hsn(), col.tax(), col.amount(),
  ],
  sources: [
    { key: 'stock', label: 'Pick from stock', source: 'stock' },
    { key: 'excel', label: 'Import from Excel', source: 'excel' },
  ],
})

/**
 * Which party a document is with. The list query exposes the joined name as
 * `supplierName` or `customerName` depending on the role, so the column and the
 * filter both have to name the same one.
 */
export type PartyKind = 'supplier' | 'subcontractor' | 'customer' | null

const partyColumnKey = (kind: PartyKind): string =>
  kind === 'customer' ? 'customerName' : 'supplierName'
const partyFilterKey = (kind: PartyKind): string =>
  kind === 'customer' ? 'customerId' : 'supplierId'

/**
 * The two columns a foreign-currency document adds (docs/00).
 *
 * Off the default view — a single-currency tenant should not carry a column of
 * INR next to a column of INR — but available in the column chooser and in
 * every export, which is where somebody reconciling a supplier statement looks.
 */
export const fcyColumns = (valueLabel = 'Total'): ColumnDef[] => [
  { key: 'currencyCode', label: 'Currency', type: 'text', width: 100, optional: true },
  { key: 'totalFcy', label: `${valueLabel} (FCY)`, type: 'money', width: 150, align: 'right', optional: true },
]

/**
 * The stock documents' "Create View" catalogue: header fields the list can
 * show when somebody picks them (INV-003, INV-018, INV-023, INV-026). A
 * lookup or a fixed choice reads by its label, as on the form.
 *
 * The audit columns — submitted, approved and created by and on — are the
 * forms chassis's, shared by every list.
 */
export const opt = (key: string, label: string, type: ColumnDef['type'] = 'text', width = 160): ColumnDef =>
  ({ key, label, type, width, optional: true, ...(type === 'money' || type === 'qty' ? { align: 'right' as const } : {}) })
export const stockListExtras = (): ColumnDef[] => [
  opt('subtotal', 'Subtotal', 'money', 140),
  opt('remarks', 'Remarks', 'text', 240),
  opt('createdAt', 'Created on', 'date', 130),
]

export const baseColumns = (kind: PartyKind, label = 'Party', valueLabel = 'Total'): ColumnDef[] => [
  { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 },
  { key: 'documentDate', label: 'Document date', type: 'date', width: 128 },
  ...(kind ? [{ key: partyColumnKey(kind), label, type: 'text' as const }] : []),
  { key: 'projectName', label: 'Project', type: 'text' },
  { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
  { key: 'total', label: valueLabel, type: 'money', width: 150, align: 'right' },
]

export const baseFilters = (kind: PartyKind, label = 'Party'): FilterDef[] => [
  { key: 'documentNo', label: 'Document number', control: 'text' },
  { key: 'documentDate', label: 'Document date', control: 'dateRange' },
  { key: 'documentStatus', label: 'Status', control: 'status' },
  ...(kind ? [{ key: partyFilterKey(kind), label, control: 'lookup' as const, lookup: { entity: kind } }] : []),
  { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
]

/**
 * The state columns and filters the purchase lists share (PUR-033, SUB-001):
 * whether an order went out to the supplier, and how much of it is invoiced.
 * Worked out on the server from the document's state (purchase-lists.ts).
 */
export const sentColumn = (optional = false): ColumnDef =>
  ({ key: 'sentToSupplier', label: 'Sent to supplier', type: 'text', width: 140, ...(optional ? { optional: true } : {}) })
export const invoiceStatusColumn = (): ColumnDef =>
  ({ key: 'invoiceStatus', label: 'Invoice status', type: 'text', width: 160, optional: true })
export const sentFilter = (): FilterDef => ({
  key: 'sentToSupplier', label: 'Sent to supplier', control: 'select',
  options: [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }],
})
export const invoiceStatusFilter = (): FilterDef => ({
  key: 'invoiceStatus', label: 'Invoice status', control: 'select',
  options: [
    { value: 'NONE', label: 'Not yet invoiced' },
    { value: 'PARTIAL', label: 'Partly invoiced' },
    { value: 'FULL', label: 'Fully invoiced' },
  ],
})

export const FULL_ACTIONS = ['save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone'] as const
export const BASIC_ACTIONS = ['save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print'] as const
/** For anything a supplier or a customer receives a copy of. */
export const SENDABLE_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone', 'email',
] as const

/**
 * For the money documents an accounting system takes (docs/02 §4e).
 *
 * `Re-sync to Integration` and `Stop sync to Integration` belong on the
 * document because that is where somebody notices a voucher never arrived —
 * making them chase it through an admin screen is how it stays unnoticed.
 */
export const SYNCED_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone', 'email',
  'resync', 'stopSync', 'changeCurrency', 'changeTaxUnit',
] as const

/** The same, for the documents nobody emails out — an internal cost record. */
export const SYNCED_BASIC_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print',
  'resync', 'stopSync',
] as const
export const ALL_TABS = ['summary', 'related', 'form', 'drive'] as const
export const REVISABLE_TABS = ['summary', 'related', 'revisions', 'form', 'drive'] as const
